import { useId } from 'react'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  error?: string
  label?: string
  id?: string
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  error,
  label,
  id: providedId,
}: SelectProps) {
  const generatedId = useId()
  const id = providedId || generatedId

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  }

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  }

  const selectWrapperStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  }

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 1rem',
    fontSize: '16px',
    border: `1px solid ${error ? '#EF4444' : '#d1d5db'}`,
    borderRadius: '8px',
    backgroundColor: disabled ? '#f3f4f6' : 'white',
    color: value ? '#1a1a1a' : '#9ca3af',
    cursor: disabled ? 'not-allowed' : 'pointer',
    appearance: 'none',
    outline: 'none',
    minHeight: '44px',
    fontFamily: 'inherit',
  }

  const iconStyles: React.CSSProperties = {
    position: 'absolute',
    right: '1rem',
    pointerEvents: 'none',
    color: '#6b7280',
  }

  const errorStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#EF4444',
    marginTop: '0.25rem',
  }

  return (
    <div style={containerStyles}>
      {label && (
        <label htmlFor={id} style={labelStyles}>
          {label}
        </label>
      )}
      <div style={selectWrapperStyles}>
        <select
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          style={selectStyles}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <svg style={iconStyles} width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      {error && (
        <span id={`${id}-error`} style={errorStyles} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}
